import React, { useEffect, useState, useRef, useLayoutEffect, lazy } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
// The animated "your tools" lockup for the home hero headline
// ("Giga connects ___ and your team in Slack/Teams"). It OPENS on the literal
// words "your tools" — reading as part of the typed sentence — then cross-FADES
// into a CONTINUOUS horizontal carousel of brand logos that drifts sideways
// forever. The tiles at the left and right edges fade out behind a soft mask,
// so it reads like an endless conveyor of tools scrolling past.
//
// Key layout detail: the slot's width MORPHS between the two phases. During the
// intro it is exactly as wide as the words "your tools" (so the sentence reads
// naturally — no dead gap before "and your team"); once the carousel takes
// over, the width animates to the carousel window. The width is animated (not
// snapped) so the headline settles smoothly rather than jumping.
type Tool = {
  name: string;
  slug: string;
};
// Real stacks Giga plugs into. The carousel streams through this whole list on
// a loop, so over time you see a broad sweep of the ecosystem. It leads with
// the most widely-recognized business tools (Shopify, Figma, Google Drive,
// HubSpot) so the opening of the stream lands on familiar names. Slack is
// intentionally omitted here (it owns the platform pill on the next line, so we
// don't want to double it up).
const TOOLS: Tool[] = [{
  name: 'Shopify',
  slug: 'shopify'
}, {
  name: 'Figma',
  slug: 'figma'
}, {
  name: 'Google Drive',
  slug: 'googledrive'
}, {
  name: 'HubSpot',
  slug: 'hubspot'
}, {
  name: 'Salesforce',
  slug: 'salesforce'
}, {
  name: 'Notion',
  slug: 'notion'
}, {
  name: 'Stripe',
  slug: 'stripe'
}, {
  name: 'Gmail',
  slug: 'gmail'
}, {
  name: 'Zoom',
  slug: 'zoom'
}, {
  name: 'Asana',
  slug: 'asana'
}, {
  name: 'Linear',
  slug: 'linear'
}, {
  name: 'Airtable',
  slug: 'airtable'
}];
// Per-tile sizing, in em so the whole lockup scales with the headline.
const TILE_EM = 0.95; // tile width/height
const GAP_EM = 0.34; // space between tiles
// Width of the visible carousel window. Sized to show ~3 full tiles in the
// clear center, with a sliver of the next/previous tile fading out at each
// edge — so it always looks like more icons are streaming in. Kept constant so
// the headline never reflows once the carousel is running.
const WINDOW_EM = 4 * TILE_EM + 3 * GAP_EM; // ≈ 4.82em → 3 crisp + edges fading
function ToolIcon({
  tool
}: {
  tool: Tool;
}) {
  return <span className="relative inline-flex items-center justify-center rounded-[0.3em] bg-white shrink-0 border border-[#2B211A]/10 [box-shadow:0_1px_2px_rgba(43,33,26,0.08)]" style={{
    width: `${TILE_EM}em`,
    height: `${TILE_EM}em`
  }} title={tool.name} data-id="element-1188">
      <img src={`https://cdn.simpleicons.org/${tool.slug}`} alt="" aria-hidden="true" className="object-contain" style={{
      width: '0.6em',
      height: '0.6em'
    }} loading="lazy" onError={e => {
      const img = e.currentTarget as HTMLImageElement;
      if (!img.dataset.fallback) {
        img.dataset.fallback = '1';
        img.src = `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${tool.slug}.svg`;
      } else {
        img.style.visibility = 'hidden';
      }
    }} data-id="element-1189" />
    </span>;
}
// The continuously scrolling track. We render the tool list TWICE back-to-back
// and translate the track left by exactly one list-width, then loop — so the
// seam is invisible and the stream appears endless. One tile-step = TILE + GAP,
// so animating across exactly TOOLS.length steps lands the duplicate perfectly
// on top of the original for a seamless wrap.
function Carousel({
  reduce
}: {
  reduce: boolean;
}) {
  const STEP_EM = TILE_EM + GAP_EM;
  const oneSetEm = TOOLS.length * STEP_EM;
  // Seconds per tile — keeps the drift speed constant regardless of list length.
  const SECONDS_PER_TILE = 1.4;
  return <span className="relative block overflow-hidden" style={{
    width: `${WINDOW_EM}em`,
    height: `${TILE_EM}em`,
    // Fade the icons out toward the left and right edges so tiles dissolve
    // as they enter/leave the stream — the "endless carousel" look.
    WebkitMaskImage: 'linear-gradient(to right, transparent 0%, #000 26%, #000 74%, transparent 100%)',
    maskImage: 'linear-gradient(to right, transparent 0%, #000 26%, #000 74%, transparent 100%)'
  }} data-id="element-1190">
      <motion.span className="absolute left-0 top-0 flex items-center" style={{
      gap: `${GAP_EM}em`,
      height: `${TILE_EM}em`
    }} animate={reduce ? undefined : {
      x: [`0em`, `-${oneSetEm}em`]
    }} transition={reduce ? undefined : {
      duration: SECONDS_PER_TILE * TOOLS.length,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-1191">
        {[...TOOLS, ...TOOLS].map((tool, i) => <ToolIcon key={`${tool.slug}-${i}`} tool={tool} data-id="element-1192" />)}
      </motion.span>
    </span>;
}
// How long the opening "your tools" word holds before it transitions into the
// rotating carousel of logos.
const INTRO_MS = 1400;
export function RotatingStack() {
  const reduce = useReducedMotion();
  // Opening beat: show the words "your tools" first, then cross-fade into the
  // carousel. Skipped entirely under reduced-motion (we go straight to the
  // icons so there's no surprise movement).
  const [introDone, setIntroDone] = useState(reduce);
  // Measure the intro word so the slot can be sized to it EXACTLY during the
  // intro phase — this is what makes "Giga connects your tools and your team"
  // read as one continuous sentence (no dead gap after "your tools"). The
  // carousel phase uses the fixed WINDOW_EM width.
  const introRef = useRef<HTMLSpanElement>(null);
  const [introWidthEm, setIntroWidthEm] = useState<number | null>(null);
  // Measure SYNCHRONOUSLY before the browser paints (useLayoutEffect) so the
  // slot is already the right width on the very first painted frame. With a
  // plain useEffect the first frame rendered at the fallback WINDOW_EM width
  // and then animated DOWN to the narrower "your tools" width — and because the
  // headline is center-aligned, that shrink made the word visibly slide to the
  // right on mount. Measuring pre-paint removes that startup motion entirely.
  useLayoutEffect(() => {
    const measure = () => {
      const el = introRef.current;
      if (!el) return;
      // Convert the measured px width into em (relative to this element's font
      // size) so the slot width tracks the headline's responsive font-size.
      const fontPx = parseFloat(getComputedStyle(el).fontSize) || 16;
      setIntroWidthEm(el.getBoundingClientRect().width / fontPx);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  useEffect(() => {
    if (reduce) {
      setIntroDone(true);
      return;
    }
    const id = setTimeout(() => setIntroDone(true), INTRO_MS);
    return () => clearTimeout(id);
  }, [reduce]);
  // The slot width for each phase. Until the intro word is measured we fall
  // back to the carousel window so nothing collapses on first paint.
  const targetWidthEm = introDone ? WINDOW_EM : introWidthEm ?? WINDOW_EM;
  // Only ANIMATE the width during the intro to carousel handoff. While the
  // intro word is still showing, the width must SNAP to its measured value
  // (duration 0) so there is no horizontal travel on mount, which was the
  // slide the user saw.
  const widthTransition = introDone ? {
    duration: 0.5,
    ease: [0.16, 1, 0.3, 1] as [number, number, number, number]
  } : {
    duration: 0
  };
  // Pure cross-FADE handoff (no slide / no scale) — the words simply dissolve
  // as the icons fade in, per the requested feel. The slot width morphs between
  // the two phases at the same time, so the sentence stays tight on both
  // mobile and desktop.
  const fade = {
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    },
    exit: {
      opacity: 0
    }
  };
  return <motion.span className="relative inline-grid items-center justify-items-start align-middle not-italic" style={{
    // Center the logo row on the text's optical midline so the tiles line
    // up symmetrically with the surrounding words ("and your"). The lockup
    // is vertically MIDDLE-aligned (align-middle) rather than sitting on
    // the baseline, and we nudge it up slightly so the row of square tiles
    // (which has no descenders) reads as centered against the cap-height of
    // the text rather than dropping below it.
    transform: 'translateY(-0.08em)'
  }} animate={{
    width: `${targetWidthEm}em`
  }} initial={false} transition={widthTransition} data-id="element-1193">
      {/* Hidden measurer for the intro word — never painted, just sized. */}
      <span ref={introRef} aria-hidden="true" className="invisible absolute left-0 top-0 font-title font-medium text-[#2B211A] whitespace-nowrap" data-id="element-1194">
        your tools
      </span>

      {/* Visible animating layer. The intro word and the carousel CROSS-FADE
                 (overlap, no mode="wait") so the slot is never empty between phases. */}
      <AnimatePresence initial={false} data-id="element-1195">
        {introDone ? <motion.span key="carousel" className="col-start-1 row-start-1 inline-flex items-center justify-start" initial={fade.initial} animate={fade.animate} exit={fade.exit} transition={{
        duration: 0.5,
        ease: 'easeInOut',
        // Let the word begin fading first so the handoff feels continuous.
        delay: reduce ? 0 : 0.1
      }} data-id="element-1196">
            <Carousel reduce={!!reduce} data-id="element-1197" />
          </motion.span> : <motion.span key="intro" className="col-start-1 row-start-1 inline-flex items-center justify-start" initial={fade.initial} animate={fade.animate} exit={fade.exit} transition={{
        duration: 0.4,
        ease: 'easeInOut'
      }} data-id="element-1198">
            {/* The opening word, styled to read as part of the headline (not a
             chip) so it feels like the sentence speaking before the logos take
             over the slot. Lowercase because it sits mid-sentence right after
             "connects". */}
            <span className="font-title font-medium text-[#2B211A] whitespace-nowrap" data-id="element-1199">
              your tools
            </span>
          </motion.span>}
      </AnimatePresence>
    </motion.span>;
}